import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Panel, Input, Button, Form, Message, IconButton, Dropdown, SelectPicker } from 'rsuite'
import { ArrowUpLine, ArrowDownLine, Trash, Plus } from '@rsuite/icons'
import { useConverter } from './EditConverterList'
import projectStore from '@client/stores/projectStore'
import {
    FILTER_OPERATORS, NO_VALUE_OPERATORS, LIST_OPERATORS, filterGroupsOf, filterRefusals, joinAlias, queryRefusal,
    TFilterGroup, TFilterMode,
} from '@shared/queryFilter'

type TJoinType = 'JOIN' | 'LEFT JOIN'

interface IJoin {
    from: string
    /** the joined table's id: template sheet id or lookup table record id */
    fromId?: string
    localField: string
    foreignField: string
    as: string
    joinType: TJoinType
}

interface IFilter {
    field: string
    operator: string
    value: string
}

interface Query {
    //collection: string,
    joins: IJoin[]
    projections: string[]
    // the older plain list; the builder writes filterGroups and leaves it empty
    filters: IFilter[]
    filterGroups?: TFilterGroup[]
}

/** How an operator reads in the list; the stored value is the code. */
const OPERATOR_LABELS: Record<string, string> = {
    '=': '=', '!=': '≠', IN: 'in', 'NOT IN': 'not in', CONTAINS: 'contains',
    '>': '>', '>=': '≥', '<': '<', '<=': '≤', 'IS EMPTY': 'is empty', 'IS NOT EMPTY': 'is not empty',
}

const joinTypeOptions = [
    { label: 'JOIN', value: 'JOIN' },
    { label: 'LEFT JOIN', value: 'LEFT JOIN' },
]

export const SQLQueryBuilder: React.FC = () => {
    const { t } = useTranslation()
    //const [collection, setCollection] = useState<string>("")
    const { data, setSqlQueryBuilderHasChanged, builderTried } = useConverter()
    const [formValue, setFormValue] = useState<Query>(data?.query || { joins: [], projections: [], filters: [] })
    const [joins, setJoins] = useState<IJoin[]>(data?.query?.joins || [])
    // one group for now, "all" or "at least one" (operator, 14.10.2026); the
    // stored format holds several
    const [groups, setGroups] = useState<TFilterGroup[]>(filterGroupsOf(data?.query))
    const [projections, setProjections] = useState<string[]>(data?.query?.projections || [])
    const { data: inDataList } = projectStore.useDataLists()

    const tables = useMemo(() => {
        if (!inDataList || inDataList.length == 0) return []
        const converterLists: any = []
        inDataList?.forEach((item: any) => {
            if (item.outTableId && data.outTableId != item.outTableId) {
                converterLists.push({
                    id: item._id,
                    tableId: item.outTableId,
                    outTableId: item.outTableId,
                    name: item.outTableName,
                    label: `${item.outTableId} - ${item.outTableName}`,
                    mapping: item.mapping,
                } as never)
            } else if (!item.outTableId && item.name) {
                // a lookup table, joined by its name; the older mixed record
                // without a name cannot be addressed and is left out
                converterLists.push({
                    id: item._id,
                    tableId: item._id,
                    outTableId: '',
                    name: item.name,
                    label: `${t('Lookup table')}: ${item.name}`,
                    mapping: item.mapping,
                } as never)
            }
        })
        return converterLists
    }, [inDataList, data?.outTableId, t])

    const localFields = useMemo(() => {
        const mapping = data?.inTable?.mapping
        if (!Array.isArray(mapping) || mapping.length === 0) return []
        return mapping.map((item: any) => ({
            id: item._id,
            field: item.field,
            column: item.column,
        }))
    }, [data?.inTable?.mapping])

    const foreignFields = useMemo(() => {
        if (!tables || tables.length == 0 || joins?.filter((j) => !!j.from).length == 0) return {}
        const fieldList: any = {}
        joins.forEach((j: any) => {
            // by the id the join keeps; joins stored before carry the name only
            const item = tables.find((t: any) => (j.fromId ? t.tableId == j.fromId : t.name == j.from))
            const alias = joinAlias(j)
            if (item && alias) {
                fieldList[alias] = []
                item.mapping.forEach((m: any) => {
                    fieldList[alias].push({
                        id: m._id,
                        field: m.field,
                        column: m.column,
                    } as never)
                })
            }
        })
        return fieldList
    }, [tables, joins])

    const allTableFields = useMemo(() => {
        const list: any = []
        if (localFields.length > 0) {
            localFields.forEach((item: any) => {
                list.push({
                    id: item.id,
                    table: data.name,
                    field: data.name + '.' + item.field,
                    column: item.column,
                } as never)
            })
        }

        const fList = Object.keys(foreignFields)
        if (fList.length > 0) {
            for (let f in fList) {
                const key = fList[f]
                foreignFields[key].forEach((item: any) => {
                    list.push({
                        id: item.id,
                        table: key,
                        field: key + '.' + item.field,
                        column: item.column,
                    } as never)
                })
            }
        }

        list.sort((a: any, b: any) => {
            return a.field.localeCompare(b.field)
        })
        return list
    }, [localFields, foreignFields])

    const tableFields = useMemo(() => {
        return allTableFields.filter((item: any) => item.table != data.name)
    }, [allTableFields])

    const handleOnChange = (value: IFilter[] | IJoin[] | string[], type: string) => {
        const newFormValue = { ...formValue }
        if (type === 'join') newFormValue.joins = value as IJoin[]
        else if (type === 'projection') newFormValue.projections = value as string[]
        else if (type === 'filterGroups') {
            newFormValue.filterGroups = value as unknown as TFilterGroup[]
            newFormValue.filters = []
        }

        setFormValue(newFormValue)
        data.query = newFormValue
        setSqlQueryBuilderHasChanged((value) => value + 1)
    }

    // join

    const addJoin = () => {
        setJoins([...joins, { from: '', localField: '', foreignField: '', as: '', joinType: 'JOIN' }])
    }
    const updateJoin = (index: number, key: keyof IJoin, value: any) => {
        const newJoins = [...joins]
        newJoins[index][key] = value
        setJoins(newJoins)
        handleOnChange(newJoins, 'join')
    }
    const removeJoin = (index: number) => {
        const newJoins = joins.filter((_, i) => i !== index)
        setJoins(newJoins)
        handleOnChange(newJoins, 'join')
    }
    const moveJoin = (fromIndex: number, toIndex: number) => {
        const newJoins = [...joins]
        const [moved] = newJoins.splice(fromIndex, 1)
        newJoins.splice(toIndex, 0, moved)
        setJoins(newJoins)
        handleOnChange(newJoins, 'join')
    }

    // projection

    const addProjection = () => setProjections([...projections, ''])
    const updateProjection = (i: number, value: string) => {
        const next = [...projections]
        next[i] = value
        setProjections(next)
        handleOnChange(next, 'projection')
    }
    const removeProjection = (i: number) => {
        const next = projections.filter((_, index) => index !== i)
        setProjections(next)
        handleOnChange(next, 'projection')
    }

    // filter

    const group: TFilterGroup = groups[0] ?? { mode: 'all', filters: [] }
    const changeGroup = (next: TFilterGroup) => {
        const nextGroups = next.filters.length ? [next] : []
        setGroups(nextGroups)
        handleOnChange(nextGroups as any, 'filterGroups')
    }
    const addFilter = () => changeGroup({ ...group, filters: [...group.filters, { field: '', operator: '=', value: '' }] })
    const updateFilter = (index: number, key: keyof IFilter, value: string) =>
        changeGroup({ ...group, filters: group.filters.map((f, i) => (i === index ? { ...f, [key]: value } : f)) })
    const removeFilter = (index: number) => changeGroup({ ...group, filters: group.filters.filter((_, i) => i !== index) })
    const setMode = (mode: TFilterMode) => changeGroup({ ...group, mode })

    // the same rules the server applies (shared/queryFilter); shown at the rows
    // once a save was refused
    const ownFields = data?.inTable ? (data.inTable.mapping ?? []).map((m: any) => m.field) : null
    const rowErrors = filterRefusals({ joins, filterGroups: groups }, data.name, ownFields)
    const rowError = (index: number) => rowErrors.find((r) => r.group === 0 && r.index === index)?.message

    const validate = (): string[] => {
        const errors: string[] = []
        // if (!collection) errors.push("Collection darf nicht leer sein.")

        // joins.forEach((join, idx) => {
        //     if (!join.from || !join.localField || !join.foreignField || !join.as) {
        //         errors.push(`Join #${idx + 1} hat leere Felder.`)
        //     }
        // })

        // one alias per join, no dot, not the converted table — the server
        // refuses the same
        const refusal = queryRefusal({ joins }, data.name)
        if (refusal) errors.push(t(refusal.message, { alias: refusal.alias }))

        return errors
    }

    const query: Query = {
        /*collection,*/
        joins,
        projections: projections.filter((p) => p.trim() !== ''),
        filters: [],
        filterGroups: groups,
    }
    const validationErrors = validate()

    type PanelHeaderProps = { label: string; onClick: () => void }
    const PanelHeader = ({ label, onClick }: PanelHeaderProps) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                <span>{label}</span>
                <Button appearance="ghost" onClick={onClick}>
                    <Plus />
                </Button>
            </div>
        )
    }

    return (
        <Panel
            header={t('Query Builder')}
            className="sql-query-builder"

        >
            <Form fluid layout='horizontal' formValue={formValue}>
                {/* Base Collection */}
                {/* <Panel header="Base Collection" bordered style={{ display: 'none' }}>
                    <Form.Group>
                        <Form.Label>Base Collection</Form.Label>
                        <Input
                            value={collection}
                            onChange={(value) => setCollection(value)}
                            placeholder={t("e.g. t1")}
                        />
                    </Form.Group>
                </Panel> */}

                {/* Joins */}
                <Panel header={<PanelHeader label={t('Joins')} onClick={addJoin} />} bordered>
                    <div className="scroll-container joins">
                        {joins.map((join, index) => (
                            <Panel key={index} bordered className="builder-form">
                                {/* joinType */}
                                <Form.Group>
                                    <Form.Label>{t('Join type')}</Form.Label>
                                    <SelectPicker
                                        popupClassName="query-builder-popup"
                                        data={joinTypeOptions}
                                        value={join.joinType || null}
                                        placeholder={t('Select join type')}
                                        cleanable={false}
                                        searchable={false}
                                        onChange={(value) => value && updateJoin(index, 'joinType', value)}
                                    />
                                </Form.Group>

                                {/* from */}
                                <Form.Group>
                                    <Form.Label>{t('Foreign table')}</Form.Label>
                                    {/* <Input
                                        value={join.from}
                                        onChange={(value) => updateJoin(index, "from", value)}
                                        placeholder={t('e.g. customer_prices')}
                                    /> */}
                                    <SelectPicker
                                        popupClassName="query-builder-popup"
                                        block
                                        data={tables.map((item: any) => ({ label: item.label, value: String(item.tableId) }))}
                                        // joins stored before carry the name only
                                        value={join.fromId || tables.find((item: any) => item.name == join.from)?.tableId || null}
                                        renderValue={(_value: any, item: any) => item?.label ?? join.from}
                                        placeholder={t('Select table ...')}
                                        cleanable={false}
                                        onChange={(value) => {
                                            const item = tables.find((table: any) => String(table.tableId) == value)
                                            if (!item) return
                                            updateJoin(index, 'fromId', item.tableId)
                                            updateJoin(index, 'from', item.name)
                                        }}
                                    />
                                </Form.Group>

                                {/* as: tells two joins of the same table apart (operator, 14.10.2026) */}
                                <Form.Group>
                                    <Form.Label>{t('As (alias)')}</Form.Label>
                                    <Input
                                        name={`join-alias-${index}`}
                                        value={join.as || ''}
                                        onChange={(value) => updateJoin(index, 'as', value)}
                                        placeholder={join.from || t('optional alias name')}
                                    />
                                </Form.Group>

                                {/* foreignField */}
                                <Form.Group>
                                    <Form.Label>{t('Foreign field')}</Form.Label>
                                    <SelectPicker
                                        popupClassName="query-builder-popup"
                                        block
                                        data={(foreignFields[joinAlias(join)] ?? []).map((item: any) => ({ label: item.field, value: item.field }))}
                                        value={join.foreignField || null}
                                        renderValue={(value: any) => value}
                                        placeholder={t('Select foreign field ...')}
                                        cleanable={false}
                                        onChange={(value) => value && updateJoin(index, 'foreignField', value)}
                                    />
                                </Form.Group>

                                {/* localField */}
                                <Form.Group>
                                    <Form.Label>{t('Local field')}</Form.Label>
                                    <SelectPicker
                                        popupClassName="query-builder-popup"
                                        block
                                        data={localFields.map((item: any) => ({ label: item.field, value: item.field }))}
                                        value={join.localField || null}
                                        renderValue={(value: any) => value}
                                        placeholder={t('Select local field ...')}
                                        cleanable={false}
                                        onChange={(value) => value && updateJoin(index, 'localField', value)}
                                    />
                                </Form.Group>

                                {/* a notice, no lock: such a join may be there only to narrow the rows */}
                                {join.from && !projections.some((p) => p.startsWith(`${joinAlias(join)}.`)) && (
                                    <Message type="info" showIcon>
                                        {t('Join "{{alias}}" returns no output fields — it only narrows the rows.', { alias: joinAlias(join) })}
                                    </Message>
                                )}

                                <Panel className="actions">
                                    <div className="order-join">
                                        <IconButton
                                            icon={<ArrowUpLine />}
                                            onClick={() => index > 0 && moveJoin(index, index - 1)}
                                            disabled={index === 0}
                                        />
                                        <IconButton
                                            icon={<ArrowDownLine />}
                                            onClick={() => index < joins.length - 1 && moveJoin(index, index + 1)}
                                            disabled={index === joins.length - 1}
                                        />
                                    </div>
                                    <Button
                                        className="delete"
                                        appearance="subtle"
                                        color="red"
                                        onClick={() => removeJoin(index)}
                                    >
                                        <Trash style={{ marginRight: 8 }} />
                                        {t('Remove Join')}
                                    </Button>
                                </Panel>
                            </Panel>
                        ))}
                    </div>
                </Panel>

                {/* Projections */}
                <Panel header={<PanelHeader label={t('Projections')} onClick={addProjection} />} bordered>
                    <div className="scroll-container projections">
                        {projections.map((proj, index) => (
                            <Panel key={index} bordered>
                                <Form.Group>
                                    <Form.Label>{t('Field')}</Form.Label>
                                    <div className="projection">
                                        {/* <Input value={proj} onChange={v => updateProjection(index, v)} placeholder={t('e.g. user.name')} /> */}
                                        <SelectPicker
                                        popupClassName="query-builder-popup"
                                            block
                                            data={tableFields.map((item: any) => ({ label: item.field, value: item.field }))}
                                            value={proj || null}
                                            renderValue={(value: any) => value}
                                            placeholder={t('Select field ...')}
                                            cleanable={false}
                                            onChange={(value) => value && updateProjection(index, value)}
                                        />
                                        <Button
                                            className="delete"
                                            appearance="subtle"
                                            color="red"
                                            onClick={() => removeProjection(index)}
                                        >
                                            <Trash />
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Panel>
                        ))}
                    </div>
                </Panel>

                {/* Where: one group of conditions, "all" or "at least one" (v0.15.3) */}
                <Panel header={<PanelHeader label={t('Filters')} onClick={addFilter} />} bordered>
                    <div className="scroll-container filters">
                        {group.filters.length > 1 && (
                            <SelectPicker
                                popupClassName="query-builder-popup"
                                className="filter-mode"
                                data={[
                                    { label: t('All conditions'), value: 'all' },
                                    { label: t('At least one'), value: 'any' },
                                ]}
                                value={group.mode}
                                cleanable={false}
                                searchable={false}
                                onChange={(value) => value && setMode(value as TFilterMode)}
                            />
                        )}
                        {group.filters.map((filter, index) => (
                            <Panel key={index} bordered className="filter-row">
                                {/* two lines (operator, 14.10.2026): the field over the whole width —
                                    names like "Laender.Bezeichnung" are long —, below the operator
                                    and the value, which takes the rest; in one line the value was
                                    squeezed to nothing */}
                                <SelectPicker
                                    popupClassName="query-builder-popup"
                                    className="filter-field"
                                    block
                                    data={allTableFields.map((item: any) => ({ label: item.field, value: item.field }))}
                                    value={filter.field || null}
                                    renderValue={(value: any) => value}
                                    placeholder={t('Select field ...')}
                                    cleanable={false}
                                    onChange={(value) => value && updateFilter(index, 'field', value)}
                                />
                                <div className="filter-line">
                                    <SelectPicker
                                        popupClassName="query-builder-popup"
                                        data={FILTER_OPERATORS.map((op) => ({ label: t(OPERATOR_LABELS[op]), value: op }))}
                                        value={filter.operator || null}
                                        placeholder={t('Select operator')}
                                        cleanable={false}
                                        searchable={false}
                                        onChange={(value) => value && updateFilter(index, 'operator', value)}
                                    />
                                    {!NO_VALUE_OPERATORS.includes(filter.operator) && (
                                        <Input
                                            className="filter-value"
                                            value={filter.value}
                                            onChange={(value) => updateFilter(index, 'value', value)}
                                            placeholder={LIST_OPERATORS.includes(filter.operator) ? t('e.g. DE, AT') : t('Value')}
                                        />
                                    )}
                                    <Button className="delete" appearance="subtle" color="red" onClick={() => removeFilter(index)}>
                                        <Trash />
                                    </Button>
                                </div>
                                {builderTried && rowError(index) && (
                                    <Message type="error" showIcon className="filter-error">
                                        {t(rowError(index)!)}
                                    </Message>
                                )}
                            </Panel>
                        ))}
                    </div>
                </Panel>

                {/* Generated Query */}
                {/* <Panel header={t('Generated Query')} bordered>
                    <div className="scroll-container">
                        {validationErrors.length > 0 && (
                            <Message showIcon type="warning">
                                {validationErrors.map((error, idx) => (
                                    <div key={idx}>{error}</div>
                                ))}
                            </Message>
                        )}
                    </div>

                    <pre>{JSON.stringify(query, null, 2)}</pre>
                </Panel> */}
            </Form>
        </Panel>
    )
}
